import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Layer, Group } from '../../types';
import { generateId } from '../../utils/geometry';

interface LayersState {
  layers: Record<string, Layer>;
  /** Display / stacking order, lowest index = bottom of stack */
  layerOrder: string[];
  groups: Record<string, Group>;
}

// Cycling palette for user-created layers
const EXTRA_COLORS = ['#6366f1', '#ec4899', '#14b8a6', '#f97316', '#8b5cf6', '#06b6d4'];

const initialState: LayersState = {
  layers: {
    'layer-1': { id: 'layer-1', name: 'Equipment', visible: true, locked: false, color: '#10b981', zIndex: 1, elementIds: [] },
    'layer-3': { id: 'layer-3', name: 'Track',     visible: true, locked: false, color: '#2563eb', zIndex: 2, elementIds: [] },
    'layer-2': { id: 'layer-2', name: 'Vehicles',  visible: true, locked: false, color: '#f59e0b', zIndex: 3, elementIds: [] },
  },
  // Bottom → top: Equipment, Track, Vehicles (OHTs always above rails)
  layerOrder: ['layer-1', 'layer-3', 'layer-2'],
  groups: {},
};

const layersSlice = createSlice({
  name: 'layers',
  initialState,
  reducers: {
    addLayer: (state, action: PayloadAction<{ name: string; color?: string }>) => {
      const id = `layer-${generateId()}`;
      const idx = state.layerOrder.length;
      const color = action.payload.color ?? EXTRA_COLORS[idx % EXTRA_COLORS.length];
      state.layers[id] = {
        id,
        name: action.payload.name,
        visible: true,
        locked: false,
        color,
        zIndex: idx + 1,
        elementIds: [],
      };
      state.layerOrder.push(id);
    },

    removeLayer: (state, action: PayloadAction<string>) => {
      // Elements keep their layerId; they'll just show as "unassigned" in UI.
      delete state.layers[action.payload];
      state.layerOrder = state.layerOrder.filter((id) => id !== action.payload);
      // Re-number zIndex
      state.layerOrder.forEach((id, i) => {
        if (state.layers[id]) state.layers[id].zIndex = i + 1;
      });
    },

    updateLayer: (state, action: PayloadAction<{ id: string; updates: Partial<Layer> }>) => {
      const { id, updates } = action.payload;
      if (state.layers[id]) state.layers[id] = { ...state.layers[id], ...updates };
    },

    toggleLayerVisible: (state, action: PayloadAction<string>) => {
      const l = state.layers[action.payload];
      if (l) l.visible = !l.visible;
    },

    toggleLayerLocked: (state, action: PayloadAction<string>) => {
      const l = state.layers[action.payload];
      if (l) l.locked = !l.locked;
    },

    reorderLayers: (state, action: PayloadAction<string[]>) => {
      state.layerOrder = action.payload;
      action.payload.forEach((id, i) => {
        if (state.layers[id]) state.layers[id].zIndex = i + 1;
      });
    },

    // ---- Group management ----

    createGroup: (state, action: PayloadAction<{ name: string; elementIds: string[] }>) => {
      const id = `group-${generateId()}`;
      state.groups[id] = {
        id,
        name: action.payload.name,
        locked: false,
        elementIds: [...action.payload.elementIds],
      };
      // Return the new id so callers can update element metadata
      // (redux reducers can't return values easily, caller reads via selector after dispatch)
    },

    disbandGroup: (state, action: PayloadAction<string>) => {
      delete state.groups[action.payload];
    },

    toggleGroupLocked: (state, action: PayloadAction<string>) => {
      const g = state.groups[action.payload];
      if (g) g.locked = !g.locked;
    },

    renameGroup: (state, action: PayloadAction<{ id: string; name: string }>) => {
      const g = state.groups[action.payload.id];
      if (g) g.name = action.payload.name;
    },

    addElementsToGroup: (
      state,
      action: PayloadAction<{ groupId: string; elementIds: string[] }>
    ) => {
      const g = state.groups[action.payload.groupId];
      if (!g) return;
      const existing = new Set(g.elementIds);
      action.payload.elementIds.forEach((id) => existing.add(id));
      g.elementIds = Array.from(existing);
    },

    removeElementsFromGroups: (state, action: PayloadAction<string[]>) => {
      const toRemove = new Set(action.payload);
      Object.values(state.groups).forEach((g) => {
        g.elementIds = g.elementIds.filter((id) => !toRemove.has(id));
      });
      // Disband empty groups
      Object.keys(state.groups).forEach((id) => {
        if (state.groups[id].elementIds.length === 0) delete state.groups[id];
      });
    },

    loadLayersState: (
      state,
      action: PayloadAction<{
        layers?: Record<string, Layer>;
        layerOrder?: string[];
        groups?: Record<string, Group>;
      }>
    ) => {
      if (action.payload.layers) state.layers = action.payload.layers;
      if (action.payload.layerOrder) state.layerOrder = action.payload.layerOrder;
      if (action.payload.groups) state.groups = action.payload.groups;
    },
  },
});

export const {
  addLayer,
  removeLayer,
  updateLayer,
  toggleLayerVisible,
  toggleLayerLocked,
  reorderLayers,
  createGroup,
  disbandGroup,
  toggleGroupLocked,
  renameGroup,
  addElementsToGroup,
  removeElementsFromGroups,
  loadLayersState,
} = layersSlice.actions;

export default layersSlice.reducer;

/**
 * Default layer id to assign when dropping an element based on its type.
 * Track is on top (layer-3), vehicles in middle (layer-2), equipment at base (layer-1).
 */
export const DEFAULT_LAYER_FOR_TYPE: Record<string, string> = {
  'overhead-rail': 'layer-3',
  'overhead-curve': 'layer-3',
  'cross-rail': 'layer-3',
  'oht-vehicle':   'layer-2',
  stocker:         'layer-1',
  stb:             'layer-1',
  ohb:             'layer-1',
  lifter:          'layer-1',
  rectangle:       'layer-1',
  circle:          'layer-1',
  text:            'layer-1',
};
